import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Plus } from '@phosphor-icons/react'
import { useAuth } from '../../hooks/useAuth'
import { useVisionBoards } from '../../hooks/useVisionBoards'
import { toMobileCanvasState } from './mobileBoard'
import { MobileBoardPreview } from './MobileBoardPreview'
import { MobileAuthenticatedTopBar } from './MobileAuthenticatedTopBar'
import type { Database } from '../../types/database'
import {
  getBoardDisplayTitle,
  getBoardPhotoCount,
  hasBoardGalleryPreview,
  hasRenderableBoardThumbnail,
  isRenderableBoardGalleryImageSrc,
  shouldRenderMobileBoardStatePreview,
} from '../../utils/boardGallery'
import { CANVAS_COLORS } from '../../utils/tokens/canvas'
type VisionBoard = Database['public']['Tables']['vision_boards']['Row']

const formatBoardDate = (value: string | null | undefined) =>
  new Date(value ?? '').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })

const getOpenBoardAriaLabel = (title: string | null | undefined) =>
  `Open board ${getBoardDisplayTitle(title)}`

const preloadImage = (src: string) =>
  new Promise<void>((resolve) => {
    const image = new Image()

    const finish = () => resolve()

    image.decoding = 'async'
    image.onload = () => {
      if (typeof image.decode === 'function') {
        image
          .decode()
          .catch(() => undefined)
          .finally(finish)
        return
      }

      finish()
    }
    image.onerror = finish
    image.src = src

    if (image.complete) {
      if (typeof image.decode === 'function') {
        image
          .decode()
          .catch(() => undefined)
          .finally(finish)
      } else {
        finish()
      }
    }
  })

const getSafeMobilePreviewState = (board: VisionBoard) => {
  const state = toMobileCanvasState(board.canvas_data)

  return {
    ...state,
    elements: state.elements.filter((element) =>
      isRenderableBoardGalleryImageSrc(element.src)
    ),
  }
}

const boardThumbnailShellClassName =
  'relative aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-[12px] border border-[rgba(61,59,54,0.08)]'

const BoardListThumbnail: React.FC<{ board: VisionBoard }> = ({ board }) => {
  const [imageFailed, setImageFailed] = useState(false)
  const previewState = getSafeMobilePreviewState(board)

  if (!imageFailed && hasRenderableBoardThumbnail(board)) {
    return (
      <img
        src={board.thumbnail_url ?? undefined}
        alt={getBoardDisplayTitle(board.title)}
        className="h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
    )
  }

  if (
    shouldRenderMobileBoardStatePreview(board) &&
    previewState.elements.length > 0
  ) {
    return (
      <MobileBoardPreview
        state={previewState}
        className="h-full w-full !rounded-none !border-none [aspect-ratio:auto]"
        emptyMessage=""
        shellStyle={{ boxShadow: 'none' }}
      />
    )
  }

  return (
    <div
      className="h-full w-full"
      style={{ backgroundColor: CANVAS_COLORS.background.board }}
    />
  )
}

const MobileBoardRowSkeleton: React.FC = () => (
  <div
    className="flex items-center gap-4 rounded-[24px] border border-stone-200 bg-white/85 p-4 shadow-[0_14px_32px_rgba(61,59,54,0.06)]"
    data-testid="mobile-board-row-skeleton"
    aria-hidden="true"
  >
    <div
      className={boardThumbnailShellClassName}
      style={{ backgroundColor: CANVAS_COLORS.background.board }}
    />
    <div className="min-w-0 flex-1 space-y-2">
      <div className="h-4 w-2/3 rounded-full bg-background-100" />
      <div className="h-3 w-1/2 rounded-full bg-background-100" />
    </div>
  </div>
)

export const MobileBoardsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { boards, loading } = useVisionBoards(user?.id)
  const [settledBoards, setSettledBoards] = useState<VisionBoard[]>([])
  const [listReady, setListReady] = useState(false)

  const activeBoards = useMemo(
    () =>
      [...boards].filter(hasBoardGalleryPreview).sort((a, b) => {
        return (
          new Date(b.updated_at || b.created_at).getTime() -
          new Date(a.updated_at || a.created_at).getTime()
        )
      }),
    [boards]
  )

  const activeBoardsSignature = useMemo(
    () =>
      activeBoards
        .map(
          (board) =>
            `${board.id}:${board.updated_at}:${board.thumbnail_url ?? ''}:${board.title ?? ''}`
        )
        .join('|'),
    [activeBoards]
  )

  useEffect(() => {
    if (loading) {
      if (settledBoards.length === 0) {
        setListReady(false)
      }
      return
    }

    if (activeBoards.length === 0) {
      setSettledBoards([])
      setListReady(true)
      return
    }

    let isActive = true
    setListReady(false)

    const nextBoards = [...activeBoards]
    const previewUrls = new Set<string>()

    nextBoards.forEach((board) => {
      if (hasRenderableBoardThumbnail(board) && board.thumbnail_url) {
        previewUrls.add(board.thumbnail_url)
      }

      if (shouldRenderMobileBoardStatePreview(board)) {
        getSafeMobilePreviewState(board).elements.forEach((element) => {
          previewUrls.add(element.src)
        })
      }
    })

    void Promise.all([...previewUrls].map(preloadImage)).then(() => {
      if (!isActive) return
      setSettledBoards(nextBoards)
      setListReady(true)
    })

    return () => {
      isActive = false
    }
  }, [activeBoards, activeBoardsSignature, loading, settledBoards.length])

  if (!loading && activeBoards.length === 0) {
    return <Navigate to="/board/new" replace />
  }

  return (
    <div className="min-h-screen bg-background-50">
      <MobileAuthenticatedTopBar />

      <main className="px-4 pb-24 pt-4">
        <div className="mx-auto flex w-full max-w-md flex-col gap-4">
          <header className="space-y-1 px-1">
            <h1 className="font-serif text-fs-700 text-ink-700">Your boards</h1>
            <p className="font-sans text-fs-300 text-ink-500">
              Pick up where you left off.
            </p>
          </header>

          {loading && settledBoards.length === 0 ? (
            <section className="space-y-3" aria-label="Loading boards">
              {Array.from({ length: 4 }).map((_, index) => (
                <MobileBoardRowSkeleton key={index} />
              ))}
            </section>
          ) : (
            <div className="relative" aria-busy={!listReady}>
              <section className="space-y-3">
                {settledBoards.map((board) =>
                  (() => {
                    const photoCount = getBoardPhotoCount(board)

                    return (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => navigate(`/board/${board.id}`)}
                        aria-label={getOpenBoardAriaLabel(board.title)}
                        className="flex w-full items-center gap-4 rounded-[24px] border border-stone-200 bg-white/85 p-4 text-left shadow-[0_14px_32px_rgba(61,59,54,0.06)] transition-transform duration-settle ease-precise active:scale-[0.99]"
                      >
                        <div
                          className={boardThumbnailShellClassName}
                          style={{
                            backgroundColor: CANVAS_COLORS.background.board,
                          }}
                        >
                          <BoardListThumbnail board={board} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate font-sans text-fs-400 font-medium text-ink-700">
                            {getBoardDisplayTitle(board.title)}
                          </p>
                          <p className="mt-1 font-sans text-fs-300 text-ink-500">
                            {formatBoardDate(
                              board.updated_at || board.created_at
                            )}{' '}
                            · {photoCount}{' '}
                            {photoCount === 1 ? 'photo' : 'photos'}
                          </p>
                        </div>
                      </button>
                    )
                  })()
                )}
              </section>
              {!listReady && settledBoards.length > 0 ? (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[28px] bg-background-50/18 transition-opacity duration-settle ease-precise"
                />
              ) : null}
            </div>
          )}

          {settledBoards.length > 0 ? (
            <button
              type="button"
              aria-label="Create new board"
              onClick={() => navigate('/board/new')}
              className="fixed bottom-6 right-6 z-20 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-400 text-background-50 shadow-[0_20px_50px_rgba(61,59,54,0.22)] transition-transform duration-settle ease-precise active:scale-[0.97] disabled:opacity-70"
            >
              <Plus className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </main>
    </div>
  )
}

export default MobileBoardsPage
